import React, {createRef, useState, useContext, useRef} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import axios from "axios";
import moment from "moment";
import __ from "lodash";
import { v4 as uuidv4 } from 'uuid';
import MaterialTable, { MTableToolbar } from "@material-table/core";
import {
  TablePagination,
  IconButton, Grid, Typography, Switch, useTheme,
} from "@material-ui/core";
import {
  AddBox,
  Email, Message,
} from "@material-ui/icons";
import PreviewIcon from '@mui/icons-material/Preview';

import SystemContext from "../../../SystemContext";
import { ValidatorColumn, ColumnWidth } from "../../../blocks/ColumnHelper";
import { useHandlingOrders, useAddHandlingOrder, useUpdateHandlingOrder, useDeleteHandlingOrder } from "../../../services/handling";
import { getTemplate, getDefaultValues } from "../../../functions/TemplateHelperFunctions"
import { defaultComponents } from '../../../controls/Material-Table-Components';
import { ComboSelect, HtmlTooltip, LightTooltip } from '../../../controls';
import HistoryDialog from "../../../controls/FormDialog";
import MessageExchangeGrid from "../../../controls/MessageExchangeGrid";
import AirportLookup from "../../Airports/components/AirportsLookup";
import AirportNameTooltip from "../../Airports/components/AirportNameTooltip";
import CompanyUuidLookup from "../../Companies/components/CompanyUuidLookup";

import HandlingOrdersValidator from "../validators/HandlingOrdersListValidator";
import AddHandlingOrderDialog from "./HandlingOrderDialogs/AddHandlingOrderDialog";
import SendHandlingOrderDialog from "./HandlingOrderDialogs/SendHandlingOrderDialog";
import HandlingOrderView from "./HandlingOrderDialogs/HandlingOrderView";
import useColour, {getStatusColour} from '../../../services/settings/useColour';
import SplitHandlingOrderDialog from './HandlingOrderDialogs/SplitHandlingOrderDialog';
import Icons from '../../../icons';
import { Stack } from '@mui/material';
import { useAddUserSetting, useUpdateUserSetting, useUserSettings } from '../../../services/settings';

const dateFormat = "DD/MM/YYYY";
const dateFormatLegs = "DD MMM";

const formatChars={
  "U": "[A-Z]",
  "A": "[A-Z0-9]",
  "a": "[A-Za-z0-9]",
  "i": "[A-Za-z0-9\\-]",
  "9": "[0-9]",
  "D": "[0-9.]",
  "-": "-",
  "x": "."
}
const historyButtons = {
  cancel:{icon: "checkCircle", label: "OK", color: "primary"}
};

const OrderDetails = (props) => {
  const {lines, hideCancelled} = props;

  const filteredLines = hideCancelled?__.filter(lines, line => !line?.lineCancelled):lines;
  if (filteredLines && Array.isArray(filteredLines) && filteredLines.length > 0) {
    return (
      <div style={{display: "inline-table"}}>
        <div style={{width: 100, display: "table-cell"}}>{filteredLines.length} flight included</div>
        <div style={{display: "table-cell"}}>
          {
            filteredLines.map(line => {
              const {direction, lineCancelled, airlineCode, flightNo, departureDate, scheduledDepartureTimeUTC, arrivalDate, scheduledArrivalTimeUTC} = line;
              let style = {display: "inline-block", padding: "0px 5px", backgroundColor: "rgb(239 255 230)", border: "1px solid #808080", borderRadius: "7px"};              
              let iconStyle = {display: "inline-block", height: 13, maxHeight: 13, overflow:"hidden", position: "relative", top:3, paddingRight: 2};              
              if (lineCancelled) {
                style['color'] = "#777777";
                style['textDecoration'] = "line-through";
                style['backgroundColor'] = "#eeeeee";
              }
              if (direction === 'IN'){
                if (!lineCancelled) style['backgroundColor'] = "rgb(230 239 255)"
                return (<div style={{display: "inline-block", padding: 2}}><div style={{...style}}><div style={{...iconStyle}}><Icons style={{fontSize: 13}}>flight_land</Icons></div><span style={{display: "inline-block", textDecoration: "inherit", minWidth: 95}}>{`Flight: ${airlineCode}-${flightNo}`}</span><span style={{display: "inline-block", textDecoration: "inherit", minWidth: 70}}>{`${moment(arrivalDate).format(dateFormatLegs)} ${scheduledArrivalTimeUTC}`}</span></div><div style={{display: "inline-block", width: 4}}>&nbsp;</div></div>);
              }
              return (<div style={{display: "inline-block", padding: 2}}><div style={{...style}}><div style={{...iconStyle}}><Icons style={{fontSize: 13}}>flight_takeoff</Icons></div><span style={{display: "inline-block", textDecoration: "inherit", minWidth: 95}}>{`Flight: ${airlineCode}-${flightNo}`}</span><span style={{display: "inline-block", textDecoration: "inherit", minWidth: 70}}>{`${moment(departureDate).format(dateFormatLegs)} ${scheduledDepartureTimeUTC}`}</span></div><div style={{display: "inline-block", width: 4}}>&nbsp;</div></div>);
            })
          }
        </div>
      </div>
    );
  } else {
    return (<></>);
  }
}

const HandlingOrdersList = (_props) => {
  const props = {
    ..._props
  }
  const {classes} = props;

  const {isAuthenticated} = useAuth0();
  const SystemData = useContext(SystemContext);
  const theme = useTheme();
  const eventBus = SystemData.eventBus;

  const [state] = useState({
    canEdit: props.canEdit,
  });
  const [addDialogStates, setAddDialogStates] = useState({
    showDialog: false,
    record: {},
    parentRecord: null,
    title: 'New Handling Order'
  });
  const [sendDialogStates, setSendDialogStates] = useState({
    showDialog: false,
    record: {},
    parentRecord: null,
    title: 'Send Handling Order'
  });
  const [defaultValues, setDefaultValues] = useState({
    defaultTemplate: null,
    defaultAttachment: null,
  });
  const [messageExchange, setMessageExchange] = useState({
    showDialog: false,
    record: null,
  });

  const year = props.year;
  const month = props.month;

  const tableRef = createRef({state: {lastEditingRow: false}});

  const handleAddHandlingOrder = useAddHandlingOrder();
  const handleUpdateHandlingOrder = useUpdateHandlingOrder();
  const handleDeleteHandlingOrder = useDeleteHandlingOrder();

  const queryClient = useQueryClient();
  const extraKeys = [year, month];

  const flags = useRef({
    settingsDone: false,
    loadedSetting: false,
  });

  const handlingOrders = useHandlingOrders({tableRef, queryClient, extraKeys});

  const colours = useColour({code: "HANDLING ORDERS TEXT"});
  const backgroundColours = useColour({code: "HANDLING ORDERS BACKGROUND"});

  const handleAddUserSetting = useAddUserSetting();
  const handleUpdateUserSetting = useUpdateUserSetting();

  const userSetting = useUserSettings({queryClient, extraKeys: ["HANDLING ORDERS LIST", "LAST VIEW STATE"], callback:() => {
    flags.current.loadedSetting = true; 
  }});

  if (userSetting.data && Array.isArray(userSetting.data)) {
    if (userSetting.data.length <= 0 && !flags.current.settingsDone) {
      flags.current.settingsDone = true;
      handleAddUserSetting.mutate({extraKeys:['HANDLING ORDERS LIST', 'LAST VIEW STATE'], record: {
        module: 'HANDLING ORDERS LIST', 
        area: 'LAST VIEW STATE', 
        values: {
          hideCancelled: false
        }
      }});
    } else if (!flags.current.settingsDone) {
      flags.current.settingsDone = true;
    }
  }

  const refreshList = async () => {
    queryClient.invalidateQueries(["HandlingOrders"]);
  }

  const toggleCancelled = () => {
    if (userSetting.data && Array.isArray(userSetting.data) && userSetting.data.length > 0) {
      const hideCancelled = !userSetting.data[0].values?.hideCancelled;
      const oldRecord = userSetting.data[0];
      const oldValues = oldRecord.values;
      const newValues = {...oldValues, hideCancelled};
      handleUpdateUserSetting.mutate({extraKeys:['HANDLING ORDERS LIST', 'LAST VIEW STATE'], record: {...oldRecord, values: newValues}, oldRecord});
    }
  }

  const handleNewOrder = async () => {
    let defaults = {};
    if ( !defaultValues.defaultTemplate || !defaultValues.defaultAttachment) {
      defaults = await getDefaultValues(SystemData, "handlingorders", "HandlingOrder");
      setDefaultValues(defaults);
    }

    const newOrderTemplate = {
      handlingOrderUuid: uuidv4(),
      airportCode: "",
      orderDate: moment().format("YYYY-MM-DD HH:mm:ss"),
      lines:[],
      status: "REQUIRED",
      tripNo: null,
      handlingOrderTemplateUuid: defaultValues.defaultTemplate || defaults.defaultTemplate,
      attachmentUuid: defaultValues.defaultAttachment || defaults.defaultAttachment,
      carbonCopy: defaultValues && __.has(defaultValues, "ccList") ? defaultValues.ccList : defaults.ccList,
    }
    setAddDialogStates({...addDialogStates, showDialog: true, record: {...(__.cloneDeep(newOrderTemplate))}, parentRecord: null, title: 'New Handling Order'});
  }

  const closeAddOrder = () => {
    setAddDialogStates({
      ...addDialogStates,
      showDialog: false,
      record: {},
      parentRecord: null,
      title: 'New Handling Order'
    });
  }

  const getOrderPeriod = (lines) => {
    let periodFrom = null;
    let periodTo = null;
    if (lines && Array.isArray(lines)) {
      lines.forEach(line => {
        if (periodFrom === null || line.departureDate < periodFrom) {
          periodFrom = line.departureDate;
        }
        if (periodTo === null || line.departureDate > periodTo) {
          periodTo = line.departureDate;
        }
      });
    }
   return({periodFrom, periodTo});
  }

  const handleSendOrderClick = async (rowData) => {
    let defaults = {};
    if ( !defaultValues.defaultTemplate || !defaultValues.defaultAttachment) {
      defaults = await getDefaultValues(SystemData, "handlingorders", "HandlingOrder");
      setDefaultValues(defaults);
    }

    const {periodFrom, periodTo} = getOrderPeriod(rowData.lines);
    const month = periodFrom && periodTo ? moment(periodFrom, "YYYY-MM-DD") : moment();
    const orderSubject = `Magma Aviation Handling Request // ${rowData.airportCode} // ${month.format("MMM-YYYY")}`;
    if (rowData.orderTemplate === undefined) {
      const template = await getTemplate(SystemData, "handlingorders", "Handling Order", "order" ,rowData.handlingOrderTemplateUuid);
      rowData["orderTemplate"] = template;
    }
    if (rowData.orderTemplate) {
      const newOrderTemplate = {
        ...rowData,
        orderDate: moment().format("YYYY-MM-DD HH:mm:ss"),
        orderSubject: orderSubject,
        carbonCopy: defaultValues && __.has(defaultValues, "ccList") ? defaultValues.ccList : defaults.ccList,
      }
      const title = `Send Handling Order${rowData ? " to '" + rowData.companyName + "'":""}${rowData.airportCode ? " at '" + rowData.airportCode + "'": ""}`;
      setSendDialogStates({...sendDialogStates, showDialog: true, record: {...(__.cloneDeep(newOrderTemplate))}, parentRecord: null, title: title});
    }
  }

  const closeSendOrder = () => {
    setSendDialogStates({
      ...addDialogStates,
      showDialog: false,
      record: {},
      parentRecord: null,
      title: 'Send Handling Order'
    });
  }

  const getColumns = (props) => {
    const validatorColumn = ValidatorColumn(HandlingOrdersValidator, true);

    const baseColumn = {
      cellStyle: { fontSize:12, padding: "4px 2px" },
      headerStyle: { padding: "4px 2px" },
      editable: props.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
      sorting: false
    };

    const defaultEditProps = {
      style:{fontSize: "0.875rem"}
    };

    return [
      {...validatorColumn},
      /*
        Can use ...ColumnWidth(70) to generate a fixed width column
      */

      {
        ...baseColumn,
        ...(ColumnWidth(110, [10,10], undefined, {paddingLeft: 12}, {paddingLeft: 12})),
        title: "At Airport",
        field: "airportCode",
        initialEditValue: "",
        sorting: true,
        editable: "never",
        editComponent: (props) => (
          <AirportLookup
            value={props.value}
            onChange={props.onChange}
          />
        ),
        render: (rowData) => (
          <LightTooltip title={<AirportNameTooltip airportCode={rowData.airportCode} />}>
            <span> {rowData.airportCode} </span>
          </LightTooltip>
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(130, undefined, undefined, {paddingLeft: 12}, {paddingLeft: 12})),
        title: "Ordered On",
        field: "orderDate",
        editable: "never",
        customFilterAndSearch: (term, rowData) => {
          const searchDate = moment.utc(rowData.orderDate).startOf('day').format('DD MMM YYYY').toUpperCase();
          const termUpper = `${term}`.toUpperCase();
          return searchDate.indexOf(termUpper) !== -1
        },
        render: rowData => {
          const displayDate = rowData.orderDate ? moment.utc(rowData.orderDate).format(dateFormat) : "Not Sent Yet";
          return (<div>{displayDate}</div>);
        },
      },
      {
        ...baseColumn,
        ...(ColumnWidth(130, undefined, undefined, {paddingLeft: 12}, {paddingLeft: 12})),
        title: "Status",
        field: "status",
        editable: state.canEdit ? "always" : "never",
        emptyValue: "",
        initialEditValue: "REQUIRED",
        fullWidth: true,
        sorting: true,
        cellStyle: (value, rowData) => {
          const backgroundColor = getStatusColour(backgroundColours, value);
          const color = getStatusColour(colours, value);
          const {cellStyle} = ColumnWidth(100);
          return ({...cellStyle, color, backgroundColor, textAlign: "center", border: `1px solid #d0d0d0`, fontWeight: 600});
        },
        render: rowData => (
          <ComboSelect
            group="HANDLING ORDER STATUS"
            subGroup=""
            value={rowData.status}
            displayOnly={true}
          />
        ),
        editComponent: (props) => {
          return (
            <ComboSelect
              group="HANDLING ORDER STATUS"
              subGroup=""
              fullWidth
              value={props.value}
              onChange={props.onChange}
            />
          )
        }
      },
      {
        ...baseColumn,
        ...(ColumnWidth(10,)),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(250, undefined, undefined, {paddingLeft: 12}, {paddingLeft: 12})),
        title: "Service Provider",
        field: "supplierUuid",
        editable: "never",
        emptyValue: null,
        initialEditValue: null,
        render: (rowData) => (
          <CompanyUuidLookup
            value={rowData.supplierUuid}
            type="render"
          />
        ),
      },
      {
        ...baseColumn,
        title: "Details",
        field: "lines",
        editable: "never",
        customFilterAndSearch: (term, rowData) => {
          const lines = rowData.lines;
          const termUpper = `${term}`.toUpperCase();
          if (lines && Array.isArray(lines) && lines.length > 0) {
            const found = lines.find(line => {
              if (`${line.airlineCode}-${line.flightNo}`.indexOf(termUpper) > -1) return true;
              if (`${moment(line.departureDate).format(dateFormatLegs)}`.toUpperCase().indexOf(termUpper) > -1) return true;
              if (`${moment(line.departureDate).format('DD/MMM')}`.toUpperCase().indexOf(termUpper) > -1) return true;
              if (`${moment(line.departureDate).format('DDMMM')}`.toUpperCase().indexOf(termUpper) > -1) return true;
              if (`${moment(line.departureDate).format('DD/MM')}`.toUpperCase().indexOf(termUpper) > -1) return true;
              return false;
            })
            return Boolean(found);
          }
          return false;
        },
        render: rowData => {
          return (
            <OrderDetails lines={rowData.lines} hideCancelled={(Array.isArray(userSetting.data) && userSetting?.data[0]?.values?.hideCancelled)||false}/>
          );
        },
      },
      {
        ...baseColumn,
        ...(ColumnWidth(10,)),
      },
    ];
  };

  return (isAuthenticated &&
    <div style={{width: "100%", marginLeft: 20, marginRight: 20}}>
      <MaterialTable
        //icons={{ DetailPanel: React.forwardRef((props, ref) => <Icon>unfold_more</Icon> )}}
        tableRef={tableRef}
        style={{
          border: "none",
          boxShadow: "none",
          borderRadius: 0,
        }}
        options={{
          addRowPosition: "first",
          actionsColumnIndex: -1,
          cellStyle: {
            padding: "8px 2px",
          },
          headerStyle: {
            backgroundColor: theme.palette.tableHeader.backgroundColor,
            color: theme.palette.tableHeader.textColor,
            padding: "2px",
          },
          toolbarButtonAlignment: "right",
          pageSize: 15,
          detailPanelType: "single",
          padding: "dense",
          //overflowY: "scroll",
        }}
        components={{
          ...defaultComponents,
          Toolbar: (toolbarProps) => {
            return (
              <div style={{ position: "relative" }}>
                <MTableToolbar {...toolbarProps} />
                {year !== 0?
                  <div style={{position: "absolute", left: 200, top: 16}}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography>Hide Cancelled</Typography>
                      <Switch checked={(Array.isArray(userSetting.data) && userSetting?.data[0]?.values?.hideCancelled)||false} onChange={toggleCancelled}/>
                    </Stack>                    
                  </div>
                  :<></>
                }
              </div>
            );
          },
          Pagination: (props) => (
            <TablePagination
              {...props}
              rowsPerPageOptions={[5, 10, 15]}
            />
          ),
        }}
        actions={[
          {
            icon: 'refresh',
            tooltip: "Refresh List",
            isFreeAction: true,
            onClick: (e) => {
              refreshList();
            }
          },
          {
            hidden: false,
            disabled: false,
            icon: () => (
              <IconButton>
                <AddBox/>
              </IconButton>
            ),
            iconProps: { style: { color: theme.palette.primary.main }},
            tooltip: "New Handling Order",
            isFreeAction: true,
            onClick: handleNewOrder
          },
          (rowData) => (
            {
              hidden: false,
              icon: () => (
                <LightTooltip title={<HandlingOrderView order={rowData} queryClient={queryClient} maxHeight={`calc(100vh - 100px)`}/>}
                  arrow
                  placement="left"
                  interactive
                  leaveDelay={300}
                >
                  <PreviewIcon  style={{ color: theme.palette.primary.main }}/>
                </LightTooltip>
              ),
              // tooltip: "Order Details",
              iconProps: { style: { color: theme.palette.primary.main }},
              isFreeAction: false,
            }
          ),
          (rowData) => (
            {
              hidden: false,
              disabled: !state.canEdit || rowData.status === "CANCELLED",
              icon: () => (
                <IconButton variant="contained" size={"small"} >
                  <Email style={(rowData.status !== "CANCELLED") ? { color: theme.palette.primary.main } : {color: '#b3b0b0'}}/>
                </IconButton>
              ),
              tooltip: "Email Order",
              iconProps: {  style: (rowData.status !== "CANCELLED") ? { color: theme.palette.primary.main } : {color: '#b3b0b0'} },
              isFreeAction: false,
              onClick: (e, rowData) => {
                handleSendOrderClick(rowData);
              }
            }
          ),
          (rowData) => (
            {
              hidden: false,
              disabled: false,
              icon: () => {return (
                <HtmlTooltip
                  rowData={rowData}
                  title={
                    <React.Fragment>
                      <Typography color="inherit">Message Exchange</Typography>
                      <div style={{ whiteSpace: "pre-wrap" }}>
                        {
                          rowData && __.has(rowData, "messageExchange") && rowData.messageExchange !== null
                            ?
                              rowData.messageExchange.map(exchange => {
                                const date = moment(exchange.messageDate, "YYYY=MM-DD").format("DD/MM/YYYY");
                                const display = `${date} - ${exchange.action}`;
                                return <div><span>{display}</span><br/></div>
                              })
                            : <></>
                        }
                      </div>
                    </React.Fragment>
                  }
                >
                  <IconButton
                    variant="contained" color="primary" size={"small"}
                  >
                    <Message style={(!rowData.messageExchange || !Array.isArray(rowData.messageExchange) || rowData.messageExchange.length <= 0) ? {color: '#b3b0b0'} : { color: theme.palette.primary.main}}/>
                  </IconButton>
                </HtmlTooltip>)},
              iconProps: { style: { color: theme.palette.primary.main }},
              isFreeAction: false,
              onClick: (e, rowData) => {
                if (rowData.messageExchange && Array.isArray(rowData.messageExchange) && rowData.messageExchange.length > 0)
                  setMessageExchange({showDialog: true, rowData: rowData});
              }
            }
          ),
          (rowData) => (
            {
              hidden: false,
              disabled: rowData.status==='CONFIRMED' || !Array.isArray(rowData.lines) || rowData.lines.length <2 ,
              icon: 'dynamic_feed',
              iconProps: { style: rowData.status==='CONFIRMED' || !Array.isArray(rowData.lines) || rowData.lines.length <2?{}:{ color: theme.palette.primary.main }},
              tooltip: `Split Order`,
              isFreeAction: false,
              onClick: (e, rowData) => {
                eventBus.trigger("SplitHandlingOrderDialog.open", {title: "Split Order", order: rowData, canEdit: state.canEdit });
              }
            }
          ),
        ]}
        title="Handling Orders"
        columns={getColumns({canEdit: state.canEdit})}
        data={(Array.isArray(userSetting.data) && userSetting?.data[0]?.values?.hideCancelled)?__.filter(handlingOrders.data, (order => order.status !== 'CANCELLED')):handlingOrders.data}
        editable={{
          isEditable: () => {
            return state.canEdit;
          },
          isDeletable: () => {
            return state.canEdit;
          },
          // onRowAdd: state.canEdit
          //   ? (newData) => new Promise((resolve, reject) => {
          //     handleAddPermit.mutate({record: newData, resolve, reject, extraKeys});
          //   })
          //   : undefined,
          onRowAdd: undefined,
          onRowUpdate: (newData, oldData) =>
            new Promise((resolve, reject) => {
              handleUpdateHandlingOrder.mutate({record: newData, oldRecord: oldData, resolve, reject, extraKeys});
            }),
          onRowDelete: (oldData) =>
            new Promise((resolve, reject) => {
              handleDeleteHandlingOrder.mutate({oldRecord: oldData, resolve, reject, extraKeys});
            }),
        }}
      />

      {addDialogStates.showDialog
        ?
          <AddHandlingOrderDialog
            dialogStates={addDialogStates}
            SystemData={SystemData}
            extraKeys={extraKeys}
            handleAdd={handleAddHandlingOrder}
            formatChars={formatChars}
            classes={classes}
            canEdit={state.canEdit}
            close={()=>closeAddOrder()}
            onChange={(record)=>{
              closeAddOrder();
              handleSendOrderClick(record);
            }}
          />
        :
          <></>
      }
      {sendDialogStates.showDialog
        ?
          <SendHandlingOrderDialog
            dialogStates={sendDialogStates}
            SystemData={SystemData}
            extraKeys={extraKeys}
            formatChars={formatChars}
            classes={classes}
            close={()=>closeSendOrder()}
          />
        :
          <></>
      }
      {messageExchange.showDialog
        ?
        <HistoryDialog
          title="Order Message Exchange"
          message=""
          open={messageExchange.showDialog}
          record={{notes: messageExchange.rowData.messageExchange}}
          dialogOptions={{maxWidth: "md", fullWidth: true}}
          buttons={historyButtons}
          onChange={(result) => {
            setMessageExchange({...messageExchange, showDialog: false, rowData: null});
          }}
        >
          <Grid container checkChildren={true} spacing={3}>
            <Grid item xs={12} checkChildren={true}>
              <MessageExchangeGrid messages={messageExchange.rowData.messageExchange}/>
            </Grid>
          </Grid>
        </HistoryDialog>
        :
        <></>
      }
      <SplitHandlingOrderDialog
        title={`Split Order`}
        SystemData={SystemData}
        extraKeys={extraKeys}
        formatChars={formatChars}
        classes={classes}
        afterSave={refreshList}
      />

    </div>
  );

}

export default HandlingOrdersList;
